import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule } from '@angular/material/stepper';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Guid } from 'guid-typescript';

import { FileInfo } from '../../../../../common/models/file';
import { DescriptionReport } from '../../../../../common/models/project';
import { ProjectService } from '../../../../../common/services/project.service';
import { FileService } from '../../../../../common/services/file.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, takeUntil } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-description',
  templateUrl: './description.component.html',
  styleUrls: ['./description.component.scss'],
  imports: [
    CommonModule,
    MatStepperModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    RouterModule
],
})
export class DescriptionComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  isHandset: boolean = false;

  descriptionReport: DescriptionReport | null = null;

  reportFiles: FileInfo[] = [];

  private generalProjectFields = [
    { label: 'Period izvještavanja - početak', property: 'reportingPeriodStart', type: 'date' },
    { label: 'Period izvještavanja - kraj', property: 'reportingPeriodEnd', type: 'date' },
    { label: 'Klasa ugovora', property: 'contractClass', type: 'text' },
    { label: 'Naziv organizacije', property: 'organizationName', type: 'text' },
    { label: 'Naziv programa/projekta', property: 'programName', type: 'text' },
    { label: 'Ovlaštena osoba', property: 'authorizedPerson', type: 'text' },
    { label: 'Voditelj programa/projekta', property: 'programManager', type: 'text' },
    { label: 'Mjesto provedbe', property: 'implementationLocation', type: 'text' },
    { label: 'Odobreni iznos', property: 'approvedAmount', type: 'currency' },
    { label: 'Utrošeni iznos', property: 'spentAmount', type: 'currency' },
  ];

  private contactFields = [
    { label: 'Adresa organizacije', property: 'organizationAddress', type: 'text' },
    { label: 'Telefon organizacije', property: 'organizationPhone', type: 'text' },
    { label: 'Fax organizacije', property: 'organizationFax', type: 'text' },
    { label: 'Odgovorna osoba za kontakt', property: 'contactPerson', type: 'text' },
    { label: 'Telefon kontakt osobe', property: 'contactPhone', type: 'text' },
    { label: 'E-mail kontakt osobe', property: 'contactEmail', type: 'text' },
    { label: 'Internetska stranica', property: 'website', type: 'text' },
  ];

  private achievementsFields = [
    { label: 'Opišite u kojoj su mjeri ciljevi programa/projekta ostvareni (sukladno prijedlogu programa/projekta za koji su odobrena bespovratna sredstva).', property: 'achievementDescription', type: 'text' },
    { label: 'Korisnici obuhvaćeni programom/projektom (spol, dob, mjesto odakle su korisnici, njihove potrebe i druge eventualne posebnosti). Na koji su način korisnici sudjelovali u projektu? Utjecaj programa/projekta na korisnike?', property: 'userImpactDescription', type: 'text' },
    { label: 'Broj korisnika obuhvaćenih programom/projektom.', property: 'numberOfUsers', type: 'text' },
    { label: 'Ako je bilo odstupanja u odnosu na prijedlog programa/projekta, navedite koji su bili razlozi, je li i na koji način ugovaratelj upoznat s promjenama, te na koji su način promjene utjecale na rezultate i ciljeve programa/projekta?', property: 'deviationReason', type: 'text' },
    { label: 'Jeste li u izvještajnom razdoblju naišli na neke značajne prepreke u provedbi programa/projekta? Ako da, kako ste ih uspjeli prevladati?', property: 'projectObstacles', type: 'text' },
  ];

  private executorsFields = [
    { label: 'Navedite broj zaposlenih uključenih u provedbu programa/projekta.', property: 'numberOfEmployees', type: 'text' },
    { label: 'Navedite broj volontera uključenih u provedbu programa/projekta.', property: 'numberOfVolunteers', type: 'text' },
    { label: 'Jesu li zaposlene nove osobe za potrebe provedbe programa/projekta?', property: 'newEmployees', type: 'bool' },
    { label: 'U kojim su aktivnostima sudjelovali volonteri na programu/projektu?', property: 'volunteerActivities', type: 'text' },
    { label: 'Je li bilo promjena vezano uz osobe koje su sudjelovale u provedbi programa/projekta u odnosu na prijedlog programa/projekta? Ako da, ukratko obrazložite razloge.', property: 'personnelChangesReason', type: 'text' },
  ];

  private budgetFields = [
    { label: 'Navedite glavne napomene vezano uz financijski izvještaj i eventualno odstupanje od planiranog proračuna programa/projekta.', property: 'deviationFromPlannedBudget', type: 'text' },
    { label: 'Je li se potrošnja financijskih sredstava odvijala prema planiranom proračunu ili ste imali teškoća? Navedite teškoće na koje ste naišli.', property: 'spendingChallenges', type: 'text' },
    { label: 'Je li osigurano sufinanciranje programa/projekta?', property: 'coFinancingDetails', type: 'text' },
  ];

  private additionalFields = [
    { label: 'Istaknite posebne uspjehe i moguće dodatne vrijednosti vezane uz dosadašnju provedbu programa/projekta.', property: 'successStory', type: 'text' },
    { label: 'Navedite ostvarenu suradnju s medijima u proteklom razdoblju ili drugim dionicima na aktivnostima vezanim uz promociju programa/projekta ili usluga korisnicima.', property: 'mediaCollaborations', type: 'text' },
    { label: 'Što su bile glavne prepreke s kojima ste se susreli tijekom provedbe programa/projekta? Jeste li ih uspješno prevladali i kako? Što biste naveli kao glavne pouke vezano uz te prepreke i načine na koje ste ih prevladali (ili razloge koji su vas u tome spriječili)?', property: 'obstaclesAndLessons', type: 'text' },
    { label: 'Koje aktivnosti planirate u narednom razdoblju?', property: 'plannedActivitiesForNextPeriod', type: 'text' },
    { label: 'Popis priloga uz opisni izvještaj (zapisnici, fotografije, članci iz medija, i sl.)', property: 'attachmentsSummary', type: 'text' },
  ];

  /**
   * Arrays to hold the actual "field + value" pairs after we load `descriptionReport`
   */
  generalProjectFieldValues: Array<{ label: string; value: any; type: string }> = [];
  contactFieldValues: Array<{ label: string; value: any; type: string }> = [];
  achievementsFieldValues: Array<{ label: string; value: any; type: string }> = [];
  executorsFieldValues: Array<{ label: string; value: any; type: string }> = [];
  budgetFieldValues: Array<{ label: string; value: any; type: string }> = [];
  additionalFieldValues: Array<{ label: string; value: any; type: string }> = [];

  get getActivities(): any[] {
    return this.descriptionReport?.activities ?? [];
  }

  constructor(
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private fileService: FileService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {

      this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        this.isHandset = result.matches;
      });

      const projectIdParam = this.route.snapshot.paramMap.get('projectId');
      const reportIdParam = this.route.snapshot.paramMap.get('reportId');

      if (!projectIdParam || !reportIdParam) {
        console.error('Missing projectId or reportId in route.');
        return;
      }

      const projectId = Guid.parse(projectIdParam);
      const reportId = Guid.parse(reportIdParam);

      this.projectService.getDescriptionReport(projectId, reportId).subscribe({
      next: (report) => {

        this.descriptionReport = report;
        this.processFields();
        this.loadReportFiles();
      },
      error: (err) => console.error('Error loading project or report', err),
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Process the field definitions, pulling values from `descriptionReport` so we can
   * easily iterate in the template with correct transformations (currency, date, bool, etc.).
   */
  private processFields(): void {
    if (!this.descriptionReport) {
      return;
    }

    this.generalProjectFieldValues = this.generalProjectFields.map(field => ({
      label: field.label,
      value: (this.descriptionReport as any)[field.property],
      type: field.type
    }));

    this.contactFieldValues = this.contactFields.map(field => ({
      label: field.label,
      value: (this.descriptionReport as any)[field.property],
      type: field.type
    }));

    this.achievementsFieldValues = this.achievementsFields.map(field => ({
      label: field.label,
      value: (this.descriptionReport as any)[field.property],
      type: field.type
    }));

    this.executorsFieldValues = this.executorsFields.map(field => ({
      label: field.label,
      value: (this.descriptionReport as any)[field.property],
      type: field.type
    }));

    this.budgetFieldValues = this.budgetFields.map(field => ({
      label: field.label,
      value: (this.descriptionReport as any)[field.property],
      type: field.type
    }));

    this.additionalFieldValues = this.additionalFields.map(field => ({
      label: field.label,
      value: (this.descriptionReport as any)[field.property],
      type: field.type
    }));
  }

  private loadReportFiles(): void {
    if (!this.descriptionReport?.fileStorageLocation) {
      return;
    }

    this.fileService.listDocuments(this.descriptionReport.fileStorageLocation).pipe(takeUntil(this.destroy$)).subscribe({
      next: (files) => {
        this.reportFiles = files;
      },
      error: (err) => {
        console.error('Error loading description report files from storage', err);
      },
    });
  }


  downloadFile(file: FileInfo) {
    this.fileService.downloadDocument(file.link).pipe(takeUntil(this.destroy$)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading file', err);
      },
    });
  }
}